import {
  DocumentModel,
  CURRENT_SCHEMA_VERSION,
  PageSettings,
  DocumentMetadata,
  DocumentSection,
} from './document.types';
import { UNIVERSITY_CONFIG } from '../../config/university.config';
import { DOCUMENT_DEFAULTS } from '../../config/document.config';

function generateId(prefix: string): string {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 6)}`;
}

export function createDefaultPageSettings(): PageSettings {
  return {
    paperSize: DOCUMENT_DEFAULTS.paperSize,
    orientation: 'portrait',
    margins: {
      topMm: DOCUMENT_DEFAULTS.marginsMm.top,
      bottomMm: DOCUMENT_DEFAULTS.marginsMm.bottom,
      leftMm: DOCUMENT_DEFAULTS.marginsMm.left,
      rightMm: DOCUMENT_DEFAULTS.marginsMm.right,
    },
    defaultFontFamily: DOCUMENT_DEFAULTS.fontFamily,
    defaultFontSizePt: DOCUMENT_DEFAULTS.fontSizePt,
    defaultLineSpacing: DOCUMENT_DEFAULTS.lineSpacing,
    showPageNumbers: true,
    pageNumberPosition: 'bottom-center',
  };
}

export function createDefaultMetadata(
  title: string = 'Untitled Document',
  documentType: DocumentMetadata['documentType'] = 'lab_report'
): DocumentMetadata {
  const now = new Date().toISOString();
  return {
    id: generateId('doc'),
    title,
    documentType,
    author: {
      studentName: '',
      studentId: '',
      sectionOrGroup: '',
    },
    instructor: {
      instructorName: '',
      designation: '',
      department: '',
    },
    institution: {
      name: UNIVERSITY_CONFIG.name,
      shortName: UNIVERSITY_CONFIG.shortName,
      department: UNIVERSITY_CONFIG.defaultDepartment,
      website: UNIVERSITY_CONFIG.website,
    },
    createdAt: now,
    modifiedAt: now,
    version: 1,
  };
}

/**
 * Creates a completely blank document
 */
export function createBlankDocument(title = 'Blank Document'): DocumentModel {
  return {
    schemaVersion: CURRENT_SCHEMA_VERSION,
    metadata: createDefaultMetadata(title, 'custom'),
    pageSettings: createDefaultPageSettings(),
    sections: [
      {
        id: generateId('sec'),
        title: 'Section 1',
        type: 'custom',
        enabled: true,
        collapsed: false,
        blocks: [
          {
            id: generateId('blk'),
            type: 'text',
            createdAt: new Date().toISOString(),
            data: { text: 'Start writing your document here...' },
          },
        ],
      },
    ],
  };
}

/**
 * Creates a standard academic Laboratory Report with pre-configured sections and sample KaTeX blocks
 */
export function createSampleLabReport(): DocumentModel {
  const now = new Date().toISOString();

  const sections: DocumentSection[] = [
    {
      id: 'sec-cover',
      title: 'Cover Page',
      type: 'cover',
      enabled: true,
      collapsed: false,
      blocks: [],
    },
    {
      id: 'sec-title',
      title: 'Experiment Title',
      type: 'title',
      enabled: true,
      collapsed: false,
      blocks: [
        {
          id: generateId('blk'),
          type: 'text',
          createdAt: now,
          data: {
            text: 'Experiment 04: Verification of Semiconductor Diode Characteristics and Dynamic Resistance Measurement',
          },
        },
      ],
    },
    {
      id: 'sec-objectives',
      title: 'Objectives',
      type: 'objectives',
      enabled: true,
      collapsed: false,
      blocks: [
        {
          id: generateId('blk'),
          type: 'text',
          createdAt: now,
          data: {
            text: '1. To understand the operational behavior and I-V characteristics of PN junction diodes.\n2. To measure the knee / barrier cut-in voltage (V_γ) under forward bias.\n3. To compute dynamic forward resistance (r_d) from the experimental slope.',
          },
        },
      ],
    },
    {
      id: 'sec-theory',
      title: 'Theory (Summary)',
      type: 'theory',
      enabled: true,
      collapsed: false,
      blocks: [
        {
          id: generateId('blk'),
          type: 'text',
          createdAt: now,
          data: {
            text: 'The current-voltage relationship of a semiconductor PN junction diode is governed by the ideal Shockley diode equation:',
          },
        },
        {
          id: generateId('blk'),
          type: 'formula',
          createdAt: now,
          data: {
            latex: 'I_D = I_S \\left( e^{\\frac{V_D}{n V_T}} - 1 \\right)',
            displayMode: true,
            equationNumber: '(Eq. 1)',
            caption: 'Ideal Shockley Diode Current Equation',
          },
        },
      ],
    },
    {
      id: 'sec-circuit',
      title: 'Circuit Diagram',
      type: 'custom',
      enabled: true,
      collapsed: false,
      blocks: [
        {
          id: generateId('blk'),
          type: 'text',
          createdAt: now,
          data: {
            text: 'Connect the 1N4007 Silicon Diode in series with a 1 kΩ current-limiting resistor across the DC power supply.',
          },
        },
      ],
    },
    {
      id: 'sec-prelab',
      title: 'Pre-Lab',
      type: 'custom',
      enabled: true,
      collapsed: false,
      blocks: [
        {
          id: generateId('blk'),
          type: 'text',
          createdAt: now,
          data: {
            text: '1. What is the standard cut-in voltage for Silicon vs Germanium diodes?\n2. State the equation for thermal voltage V_T at 300K.',
          },
        },
      ],
    },
    {
      id: 'sec-data',
      title: 'Experimental Datasheet',
      type: 'data',
      enabled: true,
      collapsed: false,
      blocks: [
        {
          id: generateId('blk'),
          type: 'table',
          createdAt: now,
          data: {
            rows: 5,
            cols: 3,
            hasHeaderRow: true,
            hasHeaderCol: false,
            borderStyle: 'academic',
            caption: 'Forward Bias Experimental Data (T = 25°C)',
            cells: [
              [
                { id: 'c00', content: 'Trial #', isHeader: true, alignment: 'center', bold: true },
                { id: 'c01', content: 'Diode Voltage V_D (V)', isHeader: true, alignment: 'center', bold: true },
                { id: 'c02', content: 'Diode Current I_D (mA)', isHeader: true, alignment: 'center', bold: true },
              ],
              [
                { id: 'c10', content: '1', alignment: 'center' },
                { id: 'c11', content: '0.20', alignment: 'center' },
                { id: 'c12', content: '0.00', alignment: 'center' },
              ],
              [
                { id: 'c20', content: '2', alignment: 'center' },
                { id: 'c21', content: '0.50', alignment: 'center' },
                { id: 'c22', content: '0.12', alignment: 'center' },
              ],
              [
                { id: 'c30', content: '3', alignment: 'center' },
                { id: 'c31', content: '0.65', alignment: 'center' },
                { id: 'c32', content: '2.45', alignment: 'center' },
              ],
              [
                { id: 'c40', content: '4', alignment: 'center' },
                { id: 'c41', content: '0.72', alignment: 'center' },
                { id: 'c42', content: '8.60', alignment: 'center' },
              ],
            ],
          },
        },
      ],
    },
    {
      id: 'sec-postlab',
      title: 'Post-Lab',
      type: 'custom',
      enabled: true,
      collapsed: false,
      blocks: [
        {
          id: generateId('blk'),
          type: 'formula',
          createdAt: now,
          data: {
            latex: 'r_d = \\frac{\\Delta V_D}{\\Delta I_D} = \\frac{0.72\\text{ V} - 0.65\\text{ V}}{8.60\\text{ mA} - 2.45\\text{ mA}} \\approx 11.38\\;\\Omega',
            displayMode: true,
            equationNumber: '(Eq. 2)',
          },
        },
      ],
    },
    {
      id: 'sec-discussion',
      title: 'Discussion',
      type: 'discussion',
      enabled: true,
      collapsed: false,
      blocks: [
        {
          id: generateId('blk'),
          type: 'text',
          createdAt: now,
          data: {
            text: 'The experiment successfully verified the exponential forward conduction curve. Minor discrepancy was attributed to resistor tolerance and meter lead resistance.',
          },
        },
      ],
    },
  ];

  return {
    schemaVersion: CURRENT_SCHEMA_VERSION,
    metadata: {
      ...createDefaultMetadata('Experiment 04: Diode Characteristics & Verification', 'lab_report'),
      courseCode: 'CSE 207',
      courseTitle: 'Electronic Circuits & Semiconductor Devices Laboratory',
    },
    pageSettings: createDefaultPageSettings(),
    sections,
  };
}
